import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Calendar, Briefcase, MapPin, X, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

const AdminJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  useEffect(() => {
    fetchJobsAndApplications();
  }, []);

  const fetchJobsAndApplications = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('token');

      const [jobsRes, appsRes] = await Promise.all([
        axios.get(`${apiUrl}/api/jobs`).catch(() => ({ data: [] })),
        axios.get(`${apiUrl}/api/admin/applications`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
      ]);

      setJobs(Array.isArray(jobsRes.data) ? jobsRes.data : jobsRes.data?.data || []);
      setApplications(Array.isArray(appsRes.data) ? appsRes.data : appsRes.data?.data || []);
    } catch (err) {
      toast.error('Failed to sync job portal data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Title and Description are required');
      return;
    }

    setIsSubmitting(true);
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      await axios.post(`${apiUrl}/api/jobs`, formData);
      toast.success('New career role published successfully!');
      setShowModal(false);
      setFormData({ title: '', description: '' });
      fetchJobsAndApplications();
    } catch (err) {
      toast.error('Failed to publish job opening');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this position?')) {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        await axios.delete(`${apiUrl}/api/jobs/${id}`);
        toast.success('Role removed from job portal');
        fetchJobsAndApplications();
      } catch (err) {
        toast.error('Failed to remove job role');
      }
    }
  };

  const getApplicationsCountForJob = (jobId) => {
    return applications.filter(app => Number(app.job_id) === Number(jobId)).length;
  };

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-sans">
      
      {/* Header Panel */}
      <div className="admin-hero-panel flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-mono mb-2">
            <Briefcase className="w-3.5 h-3.5" />
            <span>Quivix Talent Portal</span>
          </div>
          <h1 className="admin-heading text-3xl sm:text-4xl font-black text-white tracking-tight">
            Careers & Job Openings
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Publish and manage live engineering roles, remote openings, and incoming candidate applications.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="admin-mini-stat bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-center">
            <span className="text-xl font-bold text-white font-mono">{jobs.length}</span>
            <small className="block text-[10px] text-slate-400 font-mono uppercase">Open Roles</small>
          </div>
          <div className="admin-mini-stat bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-center">
            <span className="text-xl font-bold text-emerald-400 font-mono">{applications.length}</span>
            <small className="block text-[10px] text-slate-400 font-mono uppercase">Inbound CVs</small>
          </div>
          <button 
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-lg shadow-indigo-600/30 transition-all active:scale-95"
          >
            <Plus size={18} />
            <span>Publish New Role</span>
          </button>
        </div>
      </div>

      {/* Jobs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AnimatePresence mode="popLayout">
          {jobs.map((job, idx) => {
            const applicantCount = getApplicationsCountForJob(job.id);

            return (
              <motion.div 
                key={job.id || idx}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: idx * 0.04 }}
                className="glass-card p-6 flex flex-col justify-between relative overflow-hidden group hover:border-indigo-500/30 transition-all"
              >
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 shrink-0">
                        <Briefcase size={22} />
                      </div>
                      <div>
                        <h3 className="admin-heading text-lg font-bold text-white font-heading group-hover:text-indigo-400 transition-colors">
                          {job.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-[10px] font-mono text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} className="text-indigo-400" />
                            {job.posted_at ? new Date(job.posted_at).toLocaleDateString() : 'Active'}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin size={12} className="text-emerald-400" />
                            Remote / Global
                          </span>
                        </div>
                      </div>
                    </div>

                    <button 
                      onClick={() => handleDelete(job.id)}
                      className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <p className="text-xs text-slate-300 leading-relaxed line-clamp-4 mt-2">
                    {job.description}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
                  <span className="px-2.5 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-[10px] font-mono">
                    Full-Time Engineering
                  </span>
                  <span className="text-xs font-mono font-bold text-emerald-400 flex items-center gap-1">
                    <Users size={14} />
                    {applicantCount} {applicantCount === 1 ? 'Applicant' : 'Applicants'}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {!jobs.length && (
        <div className="glass-card p-12 text-center text-slate-400 font-mono text-sm">
          No job openings posted. Click "Publish New Role" above to post a role.
        </div>
      )}

      {/* Modal - Career Form */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-md" 
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 20 }}
              className="glass-panel w-full max-w-xl p-0 overflow-hidden relative z-10 border-white/10 shadow-2xl my-auto rounded-3xl"
            >
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-mono text-indigo-400 uppercase tracking-widest">Talent Acquisition</span>
                  <h2 className="admin-heading text-2xl font-bold text-white font-heading mt-0.5">Publish Career Role</h2>
                </div>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div>
                  <label className="admin-form-label mb-1.5 block">Position Title</label>
                  <input 
                    type="text" 
                    className="input-premium w-full"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g. Senior Go Infrastructure Architect"
                    required
                  />
                </div>
                <div>
                  <label className="admin-form-label mb-1.5 block">Job Description & Qualifications</label>
                  <textarea 
                    className="input-premium w-full min-h-36 resize-none"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe role responsibilities, required skills, and expectations..."
                    required
                  />
                </div>
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                  <button 
                    type="button" 
                    onClick={() => setShowModal(false)}
                    className="px-5 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium text-xs"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-lg shadow-indigo-600/30 transition-all active:scale-95"
                  >
                    <span>{isSubmitting ? 'Publishing...' : 'Broadcast Role'}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminJobsPage;
